import * as React from 'react';

import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import { useMeasureBySelector } from '@hooks/useMeasure';

import { stickyBuyButtons, stickyHidden } from '../styled';

interface UseStickyAtMainBottomResult {
  wrapperRef: React.RefObject<HTMLDivElement>;
  sentinelRef: React.RefObject<HTMLDivElement>;
}

const STICKY_THRESHOLD_DOWN = 40;
const STICKY_THRESHOLD_UP = 70;

export const useStickyAtMainBottom = (): UseStickyAtMainBottomResult => {
  const { experiment_pd_sticky_atc_button } = useFeatureFlags();

  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const sentinelRef = React.useRef<HTMLDivElement>(null);
  const lastScrollY = React.useRef(0);

  const updatePosition = React.useCallback(() => {
    const wrapper = wrapperRef.current;
    const sentinel = sentinelRef.current;
    const mainWrap = document.querySelector('#layout-main-wrap');
    const viewportHeight = window.innerHeight;

    if (!wrapper || !sentinel || !mainWrap) {
      return;
    }

    const isScrollingUp = window.scrollY < lastScrollY.current;
    lastScrollY.current = window.scrollY;

    const sentinelRect = sentinel.getBoundingClientRect();
    const threshold = isScrollingUp
      ? STICKY_THRESHOLD_UP
      : STICKY_THRESHOLD_DOWN;
    const shouldBeSticky = sentinelRect.top < viewportHeight - threshold;

    const mainWrapRect = mainWrap?.getBoundingClientRect();
    const isFooterVisible =
      mainWrapRect && viewportHeight - mainWrapRect.bottom > 0;

    const isSticky = shouldBeSticky || isFooterVisible;
    const isHidden = isFooterVisible;

    wrapper.classList.toggle(stickyBuyButtons, isSticky);
    wrapper.classList.toggle(stickyHidden, isHidden);
  }, []);

  useMeasureBySelector(
    '#layout-main-wrap',
    updatePosition,
    !experiment_pd_sticky_atc_button
  );

  React.useEffect(() => {
    if (!experiment_pd_sticky_atc_button) return;

    window.addEventListener('scroll', updatePosition, { passive: true });
    updatePosition();

    return () => {
      window.removeEventListener('scroll', updatePosition);
    };
  }, [experiment_pd_sticky_atc_button, updatePosition]);

  return {
    wrapperRef,
    sentinelRef,
  };
};
